import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Calendar, Home, Settings, Users, BarChart3, WifiOff } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/store/auth';

const fullTabs = [
  { to: '/', label: 'Главная', icon: Home },
  { to: '/calendar', label: 'Календарь', icon: Calendar },
  { to: '/clients', label: 'Клиенты', icon: Users },
  { to: '/stats', label: 'Стат.', icon: BarChart3 },
  { to: '/settings', label: 'Меню', icon: Settings },
] as const;

const viewerTabs = [
  { to: '/calendar', label: 'Календарь', icon: Calendar },
  { to: '/settings', label: 'Меню', icon: Settings },
] as const;

const gridColsClass: Record<number, string> = {
  2: 'grid-cols-2',
  5: 'grid-cols-5',
};

export const Layout = () => {
  const loc = useLocation();
  const role = useAuthStore((s) => s.master?.role);
  const tabs = role === 'viewer' ? viewerTabs : fullTabs;
  // Hide tabs on form-like screens (new/edit/reschedule) and on full-screen detail views
  const hideTabs =
    /^\/appointments\/new\/?$/.test(loc.pathname) ||
    /^\/appointments\/[^/]+\/(edit|reschedule)\/?$/.test(loc.pathname) ||
    /^\/appointments\/[^/]+\/?$/.test(loc.pathname) ||
    /^\/clients\/[^/]+\/?$/.test(loc.pathname);
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      {/* Decorative aurora background */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-x-0 top-0 -z-10 h-[60vh] bg-aurora opacity-90"
      />

      {!online && (
        <div
          className="sticky top-0 z-50 flex items-center justify-center gap-1.5 bg-warning/95 py-1.5 text-xs font-semibold text-white shadow-soft"
          style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.375rem)' }}
        >
          <WifiOff className="h-3.5 w-3.5" /> Нет связи — данные могут устареть
        </div>
      )}

      <main
        id="app-main"
        className={cn('relative mx-auto w-full max-w-2xl px-4 pt-4', !hideTabs && 'pb-tab')}
      >
        <Outlet />
      </main>

      {!hideTabs && (
        <nav
          className="fixed inset-x-0 bottom-0 z-40 border-t border-border/50 glass"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className={cn('mx-auto grid max-w-2xl px-2 py-1.5', gridColsClass[tabs.length] ?? 'grid-cols-5')}>
            {tabs.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.to === '/'}
                className={({ isActive }) =>
                  cn(
                    'group relative flex flex-col items-center justify-center gap-0.5 rounded-xl py-2 text-[11px] font-medium transition-colors',
                    isActive
                      ? 'text-primary'
                      : 'text-muted-foreground hover:text-foreground',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={cn(
                        'relative flex h-9 w-9 items-center justify-center rounded-xl transition-all',
                        isActive && 'bg-primary-soft scale-105',
                      )}
                    >
                      <t.icon
                        className={cn(
                          'h-5 w-5 transition-transform',
                          isActive && 'scale-110',
                        )}
                        strokeWidth={isActive ? 2.4 : 1.8}
                      />
                    </span>
                    <span className={cn('tracking-tight', isActive && 'font-semibold')}>
                      {t.label}
                    </span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
};
